/**
 * A ponte da planilha: carrega o SheetJS **só quando alguém importa ou exporta**.
 *
 * POR QUE SOB DEMANDA
 * A v67 embute a biblioteca inteira — **639 KB**, quase metade do arquivo — e paga esse
 * custo em toda visita, inclusive nas muitas em que ninguém encosta numa planilha. Aqui
 * ela vira um pedaço separado que o navegador só busca no primeiro clique de importar ou
 * exportar, e guarda para os seguintes.
 *
 * DE ONDE VEM A BIBLIOTECA, E POR QUE NÃO DO npm
 * O pacote `xlsx` do registro público parou na **0.18.5** — que é justamente a versão que
 * a v67 embute, e que carrega dois alertas conhecidos (poluição de protótipo em
 * `sheet_to_json`, CVE-2023-30533; e uma expressão regular explosiva, CVE-2024-22363).
 * A SheetJS saiu do npm e passou a distribuir pelo canal próprio, onde vivem as versões
 * corrigidas. Como este produto abre planilha **enviada pelo usuário**, ficar na 0.18.5
 * seria escolher o defeito conhecido de propósito. A dependência aponta para o canal
 * oficial do fornecedor (`cdn.sheetjs.com`), na 0.20.3.
 *
 * ⚠ Isso significa uma dependência por URL no `package.json`, e não por registro. É uma
 * troca consciente: o canal é o oficial do fornecedor, e a alternativa era manter uma
 * versão com alerta aberto no caminho que lê arquivo de terceiro.
 */

/** O punhado de coisas que o simulador usa. Tipar o que se usa evita depender do resto. */
export interface ApiPlanilha {
  read(dados: Uint8Array, opcoes: { type: 'array'; cellDates: boolean }): unknown;
  writeFile(wb: unknown, nome: string): void;
  utils: {
    aoa_to_sheet(linhas: unknown[][]): unknown;
    book_new(): unknown;
    book_append_sheet(wb: unknown, ws: unknown, nome: string): void;
    sheet_to_json(ws: unknown, o: { header: 1; raw: boolean; defval: string }): unknown[][];
    decode_range(ref: string): { s: { r: number; c: number }; e: { r: number; c: number } };
    encode_cell(end: { r: number; c: number }): string;
  };
}

let carregando: Promise<ApiPlanilha> | null = null;

/**
 * Busca a biblioteca uma vez e a pendura em `window.XLSX`, que é como o código portado a
 * chama. A promessa fica guardada: dois cliques rápidos em importar não baixam duas vezes.
 */
export function carregar(): Promise<ApiPlanilha> {
  if (carregando !== null) return carregando;
  carregando = import('xlsx')
    .then((mod) => {
      const api = mod as unknown as ApiPlanilha;
      (window as unknown as { XLSX?: ApiPlanilha }).XLSX = api;
      return api;
    })
    .catch((e) => {
      /* Zera para uma segunda tentativa poder acontecer. Sem isto, uma falha de rede no
         primeiro clique deixaria importar e exportar quebrados pelo resto da sessão. */
      carregando = null;
      throw new Error(
        'Não consegui carregar o leitor de planilhas. Verifique a conexão e tente de novo. ' +
        `(${e instanceof Error ? e.message : String(e)})`,
      );
    });
  return carregando;
}
